import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { X, User, ShieldCheck, FileText, Mail, GraduationCap } from "lucide-react";

const founder = {
  name: "Sajiith Vivan",
  role: "Founder & Builder, SilentScan AI",
  bio: "SilentScan AI is built by Sajiith Vivan, a student entrepreneur passionate about accessible preventive healthcare. The project combines responsible AI, Indian nutrition science, and mental-wellness awareness into a single free tool designed to help people notice health signals before they become symptoms.",
  mission:
    "Make preventive health awareness accessible, explainable and respectful for every Indian family — with clear disclaimers, no diagnosis claims, and real human care at the centre.",
};

const privacy = [
  "We do not sell, share or advertise your data. Ever.",
  "Your responses are used only to compute your personal Health Score, Mental Wellness insights and diet plan for the current session.",
  "We do not require login. Sessions are anonymised.",
  "AI prompts are sent to Google's Gemini API purely to generate your assessment — no marketing profiles are built.",
  "You can request deletion of any session data by contacting the founder below.",
];

const terms = [
  "SilentScan AI is a preventive health & wellness AWARENESS tool. It is not a medical device.",
  "It does not diagnose diseases or replace qualified healthcare or mental-health professionals.",
  "Always consult a licensed doctor or dietitian for medical decisions. Do not stop or change any medication based on this tool.",
  "By using SilentScan AI you agree that the insights are educational, non-diagnostic, and provided as-is with no warranties.",
  "If you are experiencing a medical emergency, call 108 or visit the nearest hospital immediately.",
];

export default function AboutLegal() {
  const [tab, setTab] = useState("about");

  return (
    <section
      id="about-founder"
      data-testid="about-legal-section"
      className="relative py-28 md:py-36"
    >
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-400 mb-4">
            About & Legal
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-[-0.03em] leading-tight">
            Transparent, by design.
          </h2>
        </div>

        <div className="flex justify-center flex-wrap gap-2 mb-8">
          <Tab
            id="about"
            label="About Founder"
            icon={User}
            active={tab === "about"}
            onClick={() => setTab("about")}
          />
          <Tab
            id="privacy"
            label="Privacy Policy"
            icon={ShieldCheck}
            active={tab === "privacy"}
            onClick={() => setTab("privacy")}
          />
          <Tab
            id="terms"
            label="Terms of Use"
            icon={FileText}
            active={tab === "terms"}
            onClick={() => setTab("terms")}
          />
        </div>

        <div className="glass-card rounded-3xl p-8 sm:p-10 border border-white/10">
          {tab === "about" && (
            <div data-testid="tab-panel-about">
              <div className="flex items-start gap-5">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center shrink-0 shadow-[0_0_25px_rgba(0,229,255,0.35)]">
                  <GraduationCap className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className="font-display text-xl font-semibold">
                    {founder.name}
                  </div>
                  <div className="text-xs text-cyan-300 uppercase tracking-widest mt-1">
                    {founder.role}
                  </div>
                  <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
                    {founder.bio}
                  </p>
                </div>
              </div>
              <div className="mt-6 rounded-2xl border border-cyan-400/20 bg-cyan-500/[0.05] p-4">
                <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold mb-1">
                  Our mission
                </div>
                <p className="text-sm text-zinc-200 leading-relaxed">
                  {founder.mission}
                </p>
              </div>
              <div className="mt-6 text-sm text-zinc-400 flex items-center gap-2">
                <Mail className="w-4 h-4 text-cyan-400" />
                Reach out:{" "}
                <a
                  href="mailto:nutriplate07@gmail.com"
                  className="text-cyan-300 hover:underline"
                >
                  nutriplate07@gmail.com
                </a>
              </div>
            </div>
          )}

          {tab === "privacy" && (
            <div data-testid="tab-panel-privacy">
              <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold mb-3">
                What we do — and don't do — with your data
              </div>
              <ul className="space-y-3">
                {privacy.map((p, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm text-zinc-200 leading-relaxed"
                  >
                    <ShieldCheck className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-zinc-500">
                Last updated: December 2026. SilentScan AI is a free preventive
                health & wellness awareness product.
              </p>
            </div>
          )}

          {tab === "terms" && (
            <div data-testid="tab-panel-terms">
              <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold mb-3">
                Terms of Use
              </div>
              <ul className="space-y-3">
                {terms.map((t, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm text-zinc-200 leading-relaxed"
                  >
                    <FileText className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-zinc-500">
                By continuing to use SilentScan AI you agree to these terms.
                Effective December 2026.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

const Tab = ({ label, icon: Icon, active, onClick }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-medium transition-all ${
      active
        ? "bg-gradient-to-r from-[#0B3D91] to-cyan-500 text-white shadow-[0_0_20px_rgba(0,229,255,0.35)]"
        : "btn-outline-glass text-zinc-300"
    }`}
  >
    <Icon className="w-3.5 h-3.5" />
    {label}
  </button>
);
